import {
  call,
  CallEffect,
  delay,
  put,
  PutEffect,
  race,
  RaceEffect,
  select,
  SelectEffect,
  take,
  TakeEffect
} from 'redux-saga/effects';
import { replace } from 'connected-react-router';

import {
  LOAD_SCRIBE_BY_ID_FAILED,
  LOAD_SCRIBE_BY_ID_SUCCESS,
  LoadScribeByIdFailedAction,
  LoadScribeByIdSuccessAction,
  SetActiveScribeAction,
  UPDATE_SCRIBE_SUCCESS,
  loadScribeById as loadScribeByIdAction,
  prepScribeForEditorSuccess
} from 'js/actionCreators/scribeActions';
import {
  setEditorLoadingMessage,
  showError,
  applicationFontsLoaded as applicationFontsLoadedAction
} from 'js/actionCreators/uiActions';
import { ExistingScribeModel, PendingSave, RootState, UploadProvider, VSCFontAssetData } from 'js/types';
import { ROUTE_CODES } from 'js/config/routes';
import {
  GET_USER_FONTS_SUCCESS,
  getUserFonts,
  LoadUserFontsAction,
  updateBitmapFonts
} from 'js/actionCreators/fontActions';
import { sendErrorToSentry } from 'js/logging';
import preloadScribeFonts, { loadAllSelectableFonts } from 'js/shared/helpers/preloadScribeFonts';
import { FILE_CONTENT_TYPES, IMAGE_PROVIDER_TYPES } from 'js/config/consts';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { getProviderForType } from 'js/shared/providers';
import { canvasSizes } from 'js/config/defaults';
import { uploadImageAssetOnLoad } from 'js/actionCreators/imagesActions';

import { loadUserFontsSaga } from '../fontSagas';
import checkScribeFonts from '../sagaHelpers/checkScribeFonts';
import { getActiveElements } from '../selectors';
import { updateCameraCoverage } from '../sagaHelpers/updateCameraCoverage';
import sanitiseScribe from '../sagaHelpers/sanitiseScribe';
import { appliesBrokenImage } from '../sagaHelpers/replaceImageUrl';
import { processImageFileOnLoad } from '../sagaHelpers/processImageFileOnLoad';
import applyImageHeightAndWidth from '../sagaHelpers/applyImageHeightAndWidth';

function* waitForAutosaveQueueToFinish(
  projectId: number
): Generator<TakeEffect | SelectEffect, void, PendingSave | undefined> {
  let done = false;

  while (!done) {
    yield take(UPDATE_SCRIBE_SUCCESS);
    const queue = yield select((state: RootState) => state.autoSave.pendingSaveQueue[projectId]);
    if (queue && !queue.pendingSave && !queue.isSaving) {
      done = true;
    }
  }
}

type WaitForAutosaveQueueToFinishResult = {
  timeout?: boolean;
  saved?: void;
};

type WaitForProjectLoadResult = {
  failed?: LoadScribeByIdFailedAction;
  response?: LoadScribeByIdSuccessAction;
};

type GetAssetUrlResult = {
  assetId: number;
  blobUrl: string;
};

export function* prepProjectForEditor({
  scribeId
}: SetActiveScribeAction): Generator<
  | SelectEffect
  | PutEffect
  | RaceEffect<CallEffect | ReturnType<typeof waitForAutosaveQueueToFinish>>
  | RaceEffect<TakeEffect>
  | CallEffect
  | TakeEffect
  | ReturnType<typeof processImageFileOnLoad>,
  void,
  | PendingSave
  | WaitForAutosaveQueueToFinishResult
  | WaitForProjectLoadResult
  | string
  | VSCFontAssetData[]
  | string[]
  | GetAssetUrlResult[]
  | Response
  | Blob
  | Awaited<ReturnType<typeof processImageFileOnLoad>>
> {
  const queue = (yield select((state: RootState) => state.autoSave.pendingSaveQueue[scribeId])) as PendingSave;
  if (queue?.pendingSave || queue?.isSaving) {
    yield put(setEditorLoadingMessage('Waiting for previous changes to save'));
    const { timeout: pendingSaveTimeout } = (yield race({
      timeout: delay(30000),
      saved: waitForAutosaveQueueToFinish(scribeId)
    })) as WaitForAutosaveQueueToFinishResult;

    if (pendingSaveTimeout) {
      yield put(
        showError({
          message: 'There was an issue waiting for the previous changes to save',
          description: 'Please wait a moment and try loading your project again'
        })
      );
      yield put(replace(ROUTE_CODES.HOME));
      return;
    }
  }

  yield put(loadScribeByIdAction(scribeId));

  const { response, failed } = (yield race({
    failed: take(LOAD_SCRIBE_BY_ID_FAILED),
    response: take(LOAD_SCRIBE_BY_ID_SUCCESS)
  })) as WaitForProjectLoadResult;

  if (failed) {
    let description;
    if ('httpStatusCode' in failed.error && failed.error?.httpStatusCode === 404) {
      description = `That project is not in your list of available projects`;
    } else {
      description = `There was an issue loading your project. Error: ${failed.error.message}`;
    }

    yield put(
      showError({
        message: `Unable to load project with id ${scribeId}`,
        description
      })
    );
    yield put(replace(ROUTE_CODES.HOME));
    return;
  }

  const { scribe } = response as LoadScribeByIdSuccessAction;
  const isRenderer = (
    ((yield select((state: RootState) => state.router?.location?.pathname)) as string) || ''
  ).includes('/render');

  let updatedScribe = scribe;

  if (!isRenderer) {
    let errorLoadingFontAssets = false;

    try {
      yield put(setEditorLoadingMessage('Loading your fonts'));
      yield put(getUserFonts(scribeId));
      yield take(GET_USER_FONTS_SUCCESS);
      yield call(loadUserFontsSaga, { scribe } as LoadUserFontsAction);
    } catch (error) {
      errorLoadingFontAssets = true;
      console.error(error);
      sendErrorToSentry(error as Error);
    }

    if (!errorLoadingFontAssets) {
      const userFonts = (yield select((state: RootState) => state.fonts?.userFonts)) as VSCFontAssetData[];
      updatedScribe = checkScribeFonts(updatedScribe, userFonts);
    } else {
      yield put(
        showError({
          message: 'There was an issue loading some of your fonts',
          description: 'Please try refreshing.'
        })
      );
    }

    const applicationFontsLoaded = yield select(state => state.ui?.applicationFontsLoaded);
    if (!applicationFontsLoaded) {
      yield put(setEditorLoadingMessage('Loading application fonts'));
      yield call(loadAllSelectableFonts);
      yield put(applicationFontsLoadedAction());
    }

    yield put(setEditorLoadingMessage('Loading project fonts'));
    yield call(preloadScribeFonts, updatedScribe);
  }

  const activeElements = (yield select(getActiveElements)) as string[];
  for (const scene of updatedScribe.scenes) {
    updatedScribe = updateCameraCoverage(activeElements, scene, updatedScribe);
  }
  const sanitisedScribe = sanitiseScribe(updatedScribe) as ExistingScribeModel;

  const { elements = [] } = sanitisedScribe;
  const imageElements = elements.filter(el => el.type === 'Image');

  const assetIdsToSend = imageElements.reduce((acc, el) => {
    if (
      (el.image?.provider === IMAGE_PROVIDER_TYPES.USER || el.image?.provider === IMAGE_PROVIDER_TYPES.AI_LIBRARY) &&
      !!el.image.assetId &&
      typeof el.image.assetId === 'number'
    ) {
      return [...acc, el.image.assetId];
    }
    return acc;
  }, [] as number[]);

  let objectUrls: GetAssetUrlResult[] = [];
  if (assetIdsToSend.length > 0) {
    try {
      yield put(
        setEditorLoadingMessage(
          `Requesting ${assetIdsToSend.length} project image${assetIdsToSend.length > 1 ? 's' : ''}`
        )
      );
      objectUrls = (yield call(appServices.getAssetUrls, assetIdsToSend)) as GetAssetUrlResult[];
    } catch (error) {
      console.error(error);
      sendErrorToSentry(error as Error);
    }
  }

  let errorLoadingAssets = false;
  let imageLoadedCount = 0;

  yield put(
    setEditorLoadingMessage(`Loading 1/${assetIdsToSend.length} project image${assetIdsToSend.length > 1 ? 's' : ''}`)
  );

  for (const imageEl of imageElements) {
    if (imageEl.image?.provider === IMAGE_PROVIDER_TYPES.UPLOAD && imageEl._imageLoaded === false) {
      appliesBrokenImage(imageEl, imageEl.id);
    } else {
      try {
        const providerFn = getProviderForType(imageEl.image?.provider);

        let url = '';

        if (imageEl.image?.provider === IMAGE_PROVIDER_TYPES.USER && objectUrls.length > 0) {
          const blobUrl = objectUrls.find(
            oUrl => oUrl.assetId.toString() === imageEl.image?.assetId.toString()
          )?.blobUrl;

          if (blobUrl) {
            url = blobUrl;
          } else {
            url = (yield call(providerFn, imageEl.image.assetId)) as string;
          }
        } else if (imageEl.image) {
          url = (yield call(providerFn, imageEl.image.assetId)) as string;
        }

        if (imageEl.image?.contentType === FILE_CONTENT_TYPES.SVG) {
          const resp = (yield call({ fn: window.fetch, context: window }, url)) as Response;

          if (!resp.ok) throw new Error(`Unable to fetch image file. Status: ${resp.status} ${resp.statusText}`);

          const blob = (yield call({ fn: resp.blob, context: resp })) as Blob;
          const { file, recolorScribeElementProperties, viewboxScribeElementProperties, containsEmbeddedImage } =
            (yield processImageFileOnLoad({
              file: blob,
              scribeElement: imageEl
            })) as Awaited<ReturnType<typeof processImageFileOnLoad>>;

          imageEl._imageUrl = URL.createObjectURL(file);
          imageEl.containsEmbeddedImage = containsEmbeddedImage;

          if (recolorScribeElementProperties) {
            const { _recolorDefaults, recolorAvailable } = recolorScribeElementProperties;
            imageEl._recolorDefaults = _recolorDefaults;
            imageEl.recolorAvailable = recolorAvailable;
          }

          if (viewboxScribeElementProperties) {
            imageEl.viewboxAttributes = viewboxScribeElementProperties.viewboxAttributes;
          }

          if (!imageEl.height || !imageEl.width) {
            yield applyImageHeightAndWidth(imageEl, canvasSizes[scribe.canvasSize]);
          }
        } else {
          imageEl._imageUrl = url;
        }

        imageEl._imageLoaded = true;
        imageEl._imageLoading = false;
        imageLoadedCount += 1;
        yield put(
          setEditorLoadingMessage(
            `Loading ${imageLoadedCount}/${assetIdsToSend.length} project image${assetIdsToSend.length > 1 ? 's' : ''}`
          )
        );
      } catch (error) {
        imageEl._imageLoaded = false;
        imageEl._imageLoading = false;
        imageEl._imageUrl = null;
        errorLoadingAssets = true;
        console.error(error);
        sendErrorToSentry(error as Error);
      }
    }
  }

  const libraryElements = isRenderer
    ? []
    : imageElements.filter(
        imageEl =>
          imageEl.image &&
          [IMAGE_PROVIDER_TYPES.LIBRARY, IMAGE_PROVIDER_TYPES.NOUN_PROJECT].includes(
            imageEl.image.provider as UploadProvider
          )
      );

  yield put(uploadImageAssetOnLoad(libraryElements, scribeId));

  yield put(prepScribeForEditorSuccess(sanitisedScribe));

  if (errorLoadingAssets) {
    yield put(
      showError({
        message: 'There was an issue loading some of your images',
        description: 'If refreshing does not load the images correctly please try replacing the broken images.'
      })
    );
  }

  const textElements = elements.filter(el => el.type === 'Text');
  yield put(updateBitmapFonts(textElements));
}
